'use client';

import Box from '@mui/material/Box';
import { type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';
import { StatusDot, type StatusDotColor } from '../StatusDot';

export interface ActorMessageCellProps {
  /** Status dot color displayed at the start of the cell. */
  status?: StatusDotColor;
  /** Pulse the status dot. Default false. */
  pulse?: boolean;
  /** Actor message text — e.g. "Recommend approve - 1 exception". */
  message: ReactNode;
  /**
   * Confidence / score percentage rendered at the trailing edge. Pass a number (0-100) and the
   * component formats it; pass a ReactNode for full control.
   */
  trailing?: number | ReactNode;
  /**
   * Severity for the trailing confidence value. Tints the trailing text. Default 'neutral'.
   */
  trailingSeverity?: 'success' | 'warning' | 'error' | 'neutral';
  /** Sx overrides on the outer Box. */
  sx?: SxProps<Theme>;
}

function severityColor(theme: Theme, severity: ActorMessageCellProps['trailingSeverity']) {
  if (!severity || severity === 'neutral') return theme.palette.text.secondary;
  return theme.palette[severity].main;
}

export function ActorMessageCell({
  status = 'success',
  pulse = false,
  message,
  trailing,
  trailingSeverity = 'neutral',
  sx,
}: ActorMessageCellProps) {
  const theme = useTheme();

  const trailingNode =
    typeof trailing === 'number' ? (
      <Typography
        component="span"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: severityColor(theme, trailingSeverity),
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}
      >
        {Math.round(trailing)}%
      </Typography>
    ) : (
      trailing
    );

  return (
    <Box sx={composeSx({ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }, sx)}>
      <StatusDot color={status} pulse={pulse} size="small" sx={{ flexShrink: 0 }} />
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          flex: 1,
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {message}
      </Typography>
      {trailing != null && trailingNode}
    </Box>
  );
}

ActorMessageCell.displayName = 'ActorMessageCell';

export default ActorMessageCell;
